import { Kind, OperationDefinitionNode, type DocumentNode, type OperationTypeNode, print } from 'graphql';

export const DEFAULT_API_ENDPOINT = 'field-ops' as const;

export type ApiEndpointName = 'field-ops' | 'core';

export type GraphQLEndpointMap = Record<ApiEndpointName, string>;

const API_DIRECTIVE_NAME = 'api';

type BuiltRoutedOperation = {
  endpoint: ApiEndpointName;
  document: DocumentNode;
  query: string;
};

const findOperationDefinition = (document: DocumentNode, operationName?: string): OperationDefinitionNode => {
  const operationDefinitions = document.definitions.filter(
    definition => definition.kind === Kind.OPERATION_DEFINITION,
  ) as OperationDefinitionNode[];

  if (operationDefinitions.length === 0) {
    throw new Error('GraphQL document does not contain an operation definition.');
  }

  if (operationName) {
    const operation = operationDefinitions.find(definition => definition.name?.value === operationName);

    if (!operation) {
      throw new Error(`GraphQL operation "${operationName}" was not found in the document.`);
    }

    return operation;
  }

  if (operationDefinitions.length > 1) {
    throw new Error('GraphQL document contains multiple operations. Provide operationName to disambiguate routing.');
  }

  return operationDefinitions[0];
};

const readApiDirectiveValue = (operation: OperationDefinitionNode): string | undefined => {
  const apiDirective = operation.directives?.find(directive => directive.name.value === API_DIRECTIVE_NAME);

  if (!apiDirective) {
    return undefined;
  }

  const nameArgument = apiDirective.arguments?.find(argument => argument.name.value === 'name');

  if (!nameArgument || (nameArgument.value.kind !== Kind.STRING && nameArgument.value.kind !== Kind.ENUM)) {
    throw new Error(
      'GraphQL @api directive must declare a string or enum literal name argument, e.g. @api(name: "core") or @api(name: core).',
    );
  }

  return nameArgument.value.value;
};

const normalizeEndpointName = (endpointName: string): string => {
  if (endpointName === 'field_ops' || endpointName === 'fieldOps') {
    return 'field-ops';
  }

  return endpointName;
};

const assertEndpointName = (endpointName: string): endpointName is ApiEndpointName => {
  return endpointName === 'field-ops' || endpointName === 'core';
};

const resolveEndpoint = (operation: OperationDefinitionNode, defaultEndpoint: ApiEndpointName): ApiEndpointName => {
  const endpointNameFromDirective = readApiDirectiveValue(operation);

  if (!endpointNameFromDirective) {
    return defaultEndpoint;
  }

  const normalizedEndpointName = normalizeEndpointName(endpointNameFromDirective);

  if (!assertEndpointName(normalizedEndpointName)) {
    throw new Error(
      `Unsupported GraphQL endpoint "${endpointNameFromDirective}" in @api directive. Supported endpoints: field-ops, core.`,
    );
  }

  return normalizedEndpointName;
};

const stripApiDirective = (document: DocumentNode): DocumentNode => {
  return {
    ...document,
    definitions: document.definitions.map(definition => {
      if (definition.kind !== Kind.OPERATION_DEFINITION || !definition.directives?.length) {
        return definition;
      }

      return {
        ...definition,
        directives: definition.directives.filter(directive => directive.name.value !== API_DIRECTIVE_NAME),
      };
    }),
  };
};

export const buildRoutedOperation = (
  document: DocumentNode,
  options?: { operationName?: string; defaultEndpoint?: ApiEndpointName },
): BuiltRoutedOperation => {
  const defaultEndpoint = options?.defaultEndpoint ?? DEFAULT_API_ENDPOINT;
  const operation = findOperationDefinition(document, options?.operationName);
  const endpoint = resolveEndpoint(operation, defaultEndpoint);
  const sanitizedDocument = stripApiDirective(document);

  return {
    endpoint,
    document: sanitizedDocument,
    query: print(sanitizedDocument),
  };
};

export type RoutedGraphQLClientOptions = {
  endpoints: GraphQLEndpointMap;
  defaultEndpoint?: ApiEndpointName;
  fetchImpl?: typeof fetch;
  getHeaders?: (context: { endpoint: ApiEndpointName; operationType: OperationTypeNode }) => HeadersInit | undefined;
};

export type RoutedGraphQLRequest = {
  document: DocumentNode;
  variables?: Record<string, unknown>;
  operationName?: string;
  signal?: AbortSignal;
};

// Why a request failed: a non-2xx HTTP response, a GraphQL `errors[]` payload, or
// a 2xx with no `data`. Callers (e.g. login) classify on this — `status` /
// `graphQLErrors` let them distinguish an auth rejection from a transport failure
// without string-matching the message.
export type GraphQLRequestErrorKind = 'http' | 'graphql' | 'empty-data';

export class GraphQLRequestError extends Error {
  readonly endpoint: ApiEndpointName;
  readonly kind: GraphQLRequestErrorKind;
  readonly status?: number;
  readonly graphQLErrors?: readonly unknown[];

  constructor(
    message: string,
    init: {
      endpoint: ApiEndpointName;
      kind: GraphQLRequestErrorKind;
      status?: number;
      graphQLErrors?: readonly unknown[];
    },
  ) {
    super(message);
    this.name = 'GraphQLRequestError';
    this.endpoint = init.endpoint;
    this.kind = init.kind;
    this.status = init.status;
    this.graphQLErrors = init.graphQLErrors;
  }
}

// True when a request failed because the server rejected the request as
// unauthenticated — a 401, or a GraphQL error with an UNAUTHENTICATED code.
// Callers use this to distinguish "the token is missing/expired/invalid" from
// other transport or GraphQL failures.
export const isUnauthenticatedResponse = (error: unknown): boolean => {
  if (!(error instanceof GraphQLRequestError)) return false;
  if (error.status === 401) return true;
  return (error.graphQLErrors ?? []).some(entry => {
    const code = (entry as { extensions?: { code?: unknown } })?.extensions?.code;
    return code === 'UNAUTHENTICATED';
  });
};

export const createRoutedGraphQLClient = ({
  endpoints,
  defaultEndpoint = DEFAULT_API_ENDPOINT,
  fetchImpl = fetch,
  getHeaders,
}: RoutedGraphQLClientOptions) => {
  return {
    request: async <TData>({ document, variables, operationName, signal }: RoutedGraphQLRequest): Promise<TData> => {
      const operation = findOperationDefinition(document, operationName);
      const { endpoint, query } = buildRoutedOperation(document, { operationName, defaultEndpoint });
      const url = endpoints[endpoint];

      const response = await fetchImpl(url, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...getHeaders?.({ endpoint, operationType: operation.operation }),
        },
        body: JSON.stringify({
          query,
          variables,
          operationName,
        }),
        ...(signal ? { signal } : undefined),
      });

      if (!response.ok) {
        throw new GraphQLRequestError(
          `GraphQL request failed for endpoint "${endpoint}" with status ${response.status}.`,
          { endpoint, kind: 'http', status: response.status },
        );
      }

      const result = (await response.json()) as { data?: TData; errors?: unknown[] };

      if (result.errors?.length) {
        throw new GraphQLRequestError(
          `GraphQL request returned ${result.errors.length} error(s) for endpoint "${endpoint}".`,
          { endpoint, kind: 'graphql', graphQLErrors: result.errors },
        );
      }

      if (typeof result.data === 'undefined') {
        throw new GraphQLRequestError(`GraphQL request for endpoint "${endpoint}" did not return data.`, {
          endpoint,
          kind: 'empty-data',
        });
      }

      return result.data;
    },
  };
};
